import { GridCellParams, GridColDef, GridColumnHeaderParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import IDCell from './Cells/IDCell/IDCell';
import ProjectDeptCell from './Cells/ProjectDeptCell/ProjectNameCell';
import CustomerCell from './Cells/CustomerCell/CustomerCell';
import AffairNameCell from './Cells/AffairNameCell/AffairNameCell';
import ConsultantCell from './Cells/ConsultantCell/ConsultantCell';
import AffairOtpCell from './Cells/AffairOtpCell/AffairOtpCell';
import OrderIdCell from './Cells/OrderIdCell/OrderIdCell';
import ForcastedDateCell from './Cells/ForcastedDateCell/ForcastedDateCell';
import DeliveryManagerCell from './Cells/DeliveryManagerCell/DeliveryManagerCell';
import BmCell from './Cells/BmCell/BmCell';
import RatingCell from './Cells/RatingCell/RatingCell';
import WorkunitNameCell from './Cells/WorkunitNameCell/WorkunitNameCell';
import WorkunitReferenceCell from './Cells/WorkunitReferenceCell/WorkunitReferenceCell';
import PriceCell from './Cells/PriceCell/PriceCell';
import WorkloadCell from './Cells/WorkloadCell/WorkloadCell';
import ScopeCell from './Cells/ScopeCell/ScopeCell';
import PendingAtCell from './Cells/PendingAtCell/PendingAtCell';
import OrderStatusCell from './Cells/OrderStatusCell/OrderStatusCell';
import DeliveryStatusCell from './Cells/DeliveryStatusCell/DeliveryStatusCell';
import ClientCell from './Cells/ClientCell/ClientCell';
import RefSpecCell from './Cells/RefSpecCell/RefSpecCell';
import NameSpecCell from './Cells/NameSpecCell/NameSpecCell';
import useUserRoles from 'hooks/useUserRoles';
import { find, forEach, map } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardKpiMetric from 'types/entities/DashboardKpiMetric';
import { KpiMetricTableType } from 'types/enums/KpiMetricTableType';

// type KpiWorkunitGridData = {
//   order?: { affair?: { name?: string } };
//    order?:
// };

type KpiWorkunitGridCellParams<T> = GridCellParams<T, DashboardKpiMetric, any>;

export const columnField = {
  PROJECT_DEPT: 'bu',
  CUSTOMER: 'customer',
  AFFAIR_NAME: 'affair_name',
  CONSULTANT: 'consultant_fullname',
  AFFAIROTP: 'affair_otp',
  ORDER_ID: 'order_id',
  FORECASTED_DATE: 'forecast_date',
  DELIVERY_MANAGER: 'delivery_manager_fullname',
  BM: 'bm_id',
  RATING: 'rating',
  WORKUNIT_NAME: 'workunit_name',
  WORKUNIT_REFERENCE: 'workunit_reference',
  PRICE: 'price',
  WORKLOAD: 'workload',
  SCOPE: 'scope_id',
  PENDINGAT: 'pendingat',
  ORDER_STATUS: 'status',
  DELIVERY_STATUS: 'mission_advancement_id',
  CLIENT: 'client_fullname',
  REF_SPEC: 'ref_spec',
  NAME_SPEC: 'name_spec',
  ID: 'id',
} as const;

const defaultColumnParams: GridColDef = {
  field: '',
  headerName: '',
  align: 'left',
  headerAlign: 'center',
  filterable: false,
  description: '',
  flex: 1,
};

export const columnsParams: GridColDef[] = [
  {
    field: columnField.PROJECT_DEPT,
    headerName: 'BU',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ProjectDeptCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CUSTOMER,
    headerName: 'Customer',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <CustomerCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.NAME_SPEC,
    headerName: 'Project Name',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <NameSpecCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CONSULTANT,
    headerName: 'Consultant',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ConsultantCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.AFFAIROTP,
    headerName: 'Affair OTP',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <AffairOtpCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ORDER_ID,
    headerName: 'Order NO',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <OrderIdCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.FORECASTED_DATE,
    headerName: 'Delivery Date',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ForcastedDateCell {...params.row} />,
    minWidth: 100,
    sortable: false, // Sorting disabled
  },
  {
    field: columnField.DELIVERY_MANAGER,
    headerName: 'Delivery Manager',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <DeliveryManagerCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.BM,
    headerName: 'Business Manager',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <BmCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.RATING,
    headerName: 'kpi_rating',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <RatingCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKUNIT_NAME,
    headerName: 'Deliverable',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <WorkunitNameCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKUNIT_REFERENCE,
    headerName: 'Reference',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <WorkunitReferenceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.PRICE,
    headerName: 'Price',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <PriceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.WORKLOAD,
    headerName: 'Workload',
    renderCell: (params: KpiWorkunitGridCellParams<['charge']>) => <WorkloadCell {...params.row} />,
    minWidth: 60,
    sortable: false, // Sorting disabled
  },
  {
    field: columnField.SCOPE,
    headerName: 'Scope',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ScopeCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ORDER_STATUS,
    headerName: 'kpi_status',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <OrderStatusCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.DELIVERY_STATUS,
    headerName: 'Deliverable Status',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <DeliveryStatusCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.CLIENT,
    headerName: 'Client',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <ClientCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.REF_SPEC,
    headerName: 'Spec Reference',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <RefSpecCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.AFFAIR_NAME,
    headerName: 'Engagement name',
    renderCell: (params: KpiWorkunitGridCellParams<string>) => <AffairNameCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
  {
    field: columnField.ID,
    headerName: 'ID',
    renderCell: (params: KpiWorkunitGridCellParams<number>) => <IDCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 100,
  },
];

type ColumnsFields = { [name: string]: string[] };

const commonColumns = [
  columnField.PROJECT_DEPT,
  columnField.CUSTOMER,
  columnField.NAME_SPEC,
  columnField.CONSULTANT,
  columnField.AFFAIROTP,
  columnField.ORDER_ID,
  columnField.FORECASTED_DATE,
  columnField.DELIVERY_MANAGER,
  columnField.BM,
  columnField.RATING,
  columnField.WORKUNIT_NAME,
  columnField.WORKUNIT_REFERENCE,
  columnField.PRICE,
  columnField.WORKLOAD,
  columnField.SCOPE,
  columnField.ORDER_STATUS,
  columnField.DELIVERY_STATUS,
  columnField.CLIENT,
  columnField.REF_SPEC,
  columnField.AFFAIR_NAME,
  columnField.ID,
];

export const kpiMetricColumnsFields: ColumnsFields = {
  [KpiMetricTableType.WORKFLOW]: commonColumns,
  [KpiMetricTableType.ACCEPTANCE]: commonColumns,
  [KpiMetricTableType.PERFORMANCE]: commonColumns,
};

/**
 * React hook to build columns variable for Mui Datagrid with specific components
 */
export const useColumns = (columnsFields: ColumnsFields, order_type_id: number) => {
  const [t] = useTranslation();
  const userRoles = useUserRoles();
  // const state = useSelector((state) => state.deliverable);
  return useMemo(() => {
    const columns: { [name: string]: GridColDef<DashboardKpiMetric, any, any>[] } = {};
    forEach(columnsFields, (fields, name) => {
      columns[name] = map(fields, (field) => {
        const columnParam = find(columnsParams, ['field', field]);
        return {
          ...defaultColumnParams,
          ...columnParam,
          ...{ headerName: columnParam?.headerName ? t(columnParam.headerName) : '' },
        };
      });
    });
    return columns;
  }, [columnsFields, userRoles, t]);
};
